import { useMemo, useState } from "react";
import { toast } from "sonner";
import { FileDown } from "lucide-react";
import { Section, Empty, FilterBar } from "./CommonUI";
import { INR_P, INVOICE_TONE, fmtDate } from "./clientUtils";

export default function InvoicesTable({ rows, loading, onRefresh, projects, token, financeToken }) {
  const projMap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p.name])), [projects]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const filtered = useMemo(() => rows.filter((r) => {
    const matchesQ = !q || (`${r.invoice_number} ${projMap[r.project_id] || ""}`).toLowerCase().includes(q.toLowerCase());
    const matchesS = status === "all" || r.status === status;
    return matchesQ && matchesS;
  }), [rows, q, status, projMap]);

  const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

  const downloadInvoice = async (inv) => {
    try {
      const url = `${apiBase}/client/invoices/${inv.id}/file`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Finance-Token": financeToken,
        },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = inv?.attachment?.filename || `${inv.invoice_number || "invoice"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch {
      toast.error("Failed to download invoice file");
    }
  };

  return (
    <Section title="Invoices" subtitle="All invoices issued, with current balance and status." onRefresh={onRefresh} loading={loading}
      toolbar={<FilterBar q={q} onQ={setQ} value={status} onValue={setStatus} options={["all", "draft", "sent", "partially_paid", "paid", "overdue"]} placeholder="Search invoice #, project…" />}>
      {filtered.length === 0 ? (
        <Empty msg={rows.length === 0 ? "No invoices issued yet." : "No invoices match your filter."} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[900px]">
            <thead className="bg-[#f7f6f3] border-b border-black/10 text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">
              <tr>
                <th className="px-4 py-3 w-12">#</th>
                <th className="px-4 py-3">Invoice</th>
                <th className="px-4 py-3">Project</th>
                <th className="px-4 py-3">Issued</th>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right">Paid</th>
                <th className="px-4 py-3 text-right">Balance</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">File</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id} className="border-b border-black/5 hover:bg-[#0a0a0a]/[0.02]">
                  <td className="px-4 py-4 text-xs font-mono text-[#4a4a4a]">{String(i + 1).padStart(2, "0")}</td>
                  <td className="px-4 py-4 font-mono font-bold text-[#0a0a0a]">{r.invoice_number || "—"}</td>
                  <td className="px-4 py-4 text-sm font-semibold">{projMap[r.project_id] || "—"}</td>
                  <td className="px-4 py-4 text-xs font-mono">{fmtDate(r.issued_date)}</td>
                  <td className="px-4 py-4 text-xs font-mono">{fmtDate(r.due_date)}</td>
                  <td className="px-4 py-4 text-right font-mono font-bold">{INR_P(r.total ?? r.amount)}</td>
                  <td className="px-4 py-4 text-right font-mono text-emerald-700">{INR_P(r.paid_amount)}</td>
                  <td className={`px-4 py-4 text-right font-mono ${r.balance > 0 ? "text-rose-700 font-bold" : "text-[#4a4a4a]"}`}>{INR_P(r.balance)}</td>
                  <td className="px-4 py-4"><span className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.1em] rounded ${INVOICE_TONE[r.status] || "bg-zinc-100 text-zinc-700"}`}>{(r.status || "").replace("_", " ")}</span></td>
                  <td className="px-4 py-4 text-right">
                    {r.has_attachment ? (
                      <button onClick={() => downloadInvoice(r)} data-testid={`invoice-download-${r.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs border border-[#ff5722]/30 text-[#ff5722] rounded-full hover:bg-[#ff5722] hover:text-white transition-colors">
                        <FileDown className="h-3.5 w-3.5" /> PDF
                      </button>
                    ) : <span className="text-xs text-[#9a9a9a]">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
